import { useState } from 'react';
import { RefreshCw, Check } from 'lucide-react';

import { checkForUpdate } from '../utils/pwa';
import { usePwaUpdateStore } from '../stores/pwaUpdateStore';

const VERSION = import.meta.env.VITE_APP_VERSION || 'dev';
const SHORT = VERSION === 'dev' ? 'dev' : VERSION.slice(0, 7);

/**
 * Shows the running build and a "Check for updates" button. If a newer build
 * exists, the check surfaces it and (on a normal screen) the app updates
 * itself; otherwise it confirms you're up to date. Lets support ask
 * "what version are you on?" and lets a user force a refresh on demand.
 */
export function AppVersion({ className = '' }: { className?: string }) {
  const [checking, setChecking] = useState(false);
  const [upToDate, setUpToDate] = useState(false);

  const onCheck = async () => {
    setChecking(true);
    setUpToDate(false);
    await checkForUpdate();
    // Give the service worker a moment to surface a waiting build. If one
    // exists, UpdatePrompt applies it (a reload) — otherwise we're current.
    window.setTimeout(() => {
      setUpToDate(!usePwaUpdateStore.getState().updateReady);
      setChecking(false);
    }, 1500);
  };

  return (
    <div
      className={`flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500 ${className}`}
    >
      <span>Version {SHORT}</span>
      <span aria-hidden>·</span>
      <button
        onClick={onCheck}
        disabled={checking}
        className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-60"
      >
        <RefreshCw className={`w-3 h-3 ${checking ? 'animate-spin' : ''}`} />
        {checking ? 'Checking…' : 'Check for updates'}
      </button>
      {upToDate && (
        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
          <Check className="w-3 h-3" /> Up to date
        </span>
      )}
    </div>
  );
}
